import React from 'react';
import { Tooltip } from 'antd';
import { FD130 } from '../../../services/pwd212Api';
import { Mode } from '../hooks/usePWD212Mode';
import { isLockedForEdit, APPROVAL_STATUS } from '../helpers';

interface PWD212Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FD130 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: PWD212Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const getStatusLabel = (record: FD130 | null): string | null => {
  if (!record) return null;
  const s80 = (record.fd130_80 || '').trim();
  const s26 = (record.fd130_26 || '').trim();
  if (s26 === 'P') return '已結案';
  return APPROVAL_STATUS[s80] || null;
};

const ActionBar: React.FC<ActionBarProps> = ({
  mode, isEditing, record,
  currentIdx, allIdsLength,
  permissions,
  onAdd, onModify, onDelete, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete } = permissions;
  const canSave = mode === 'add' ? canCreate : canEdit;
  const locked = isLockedForEdit(record);
  const statusLabel = getStatusLabel(record);

  return (
    <div className="action-bar">
      {/* Navigation */}
      <div className="action-group">
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)}>最前</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)}>上一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)}>下一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)}>最後</PermBtn>
      </div>

      {/* CRUD */}
      <div className="action-group">
        <PermBtn
          allowed={isEditing ? canSave : canCreate}
          disabled={false}
          className="btn btn-primary"
          onClick={onAdd}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        <PermBtn
          allowed={isEditing ? true : canEdit}
          disabled={(!isEditing && !record) || (!isEditing && locked)}
          onClick={onModify}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        <PermBtn
          allowed={canDelete}
          disabled={isEditing || !record || locked}
          className="btn btn-danger"
          onClick={onDelete}
        >
          刪除
        </PermBtn>

        {/* Exit */}
        <div className="action-separator" />
        <button className="btn" onClick={onExit}>離開</button>
      </div>

      {statusLabel && (
        <span className="status-label" style={{
          marginLeft: 12,
          padding: '2px 10px',
          borderRadius: 4,
          fontWeight: 'bold',
          color: statusLabel === '已核准' ? '#389e0d' : statusLabel === '已結案' ? '#595959' : '#d48806',
          background: statusLabel === '已核准' ? '#f6ffed' : statusLabel === '已結案' ? '#fafafa' : '#fffbe6',
          border: `1px solid ${statusLabel === '已核准' ? '#b7eb8f' : statusLabel === '已結案' ? '#d9d9d9' : '#ffe58f'}`,
        }}>
          {statusLabel}
        </span>
      )}
    </div>
  );
};

export default React.memo(ActionBar);
